import {CalendarOutlined, CheckCircleOutlined, DeleteOutlined, EditOutlined, EnvironmentOutlined, IdcardOutlined, MailOutlined, PhoneOutlined, ShopOutlined} from '@ant-design/icons';
import {DatePicker, Divider, Drawer, Popconfirm, Select, Spin, Tag, Tooltip} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import {useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../../CommonComponents/Cards';
import {TableData} from '../../../../CommonComponents/TableData';
import {RqAccountantLookupAll, RqAccountantUpdate, RqDeleteUser} from '../../../../reactQuery/apis';
import {selectAuth} from '../../../../redux/selectors';
import {EditEmployeeScreen} from '../EditEmployee';

const {Option} = Select;

const ROLE_COLORS = {CUSTOMER: 'green', VENDOR: 'gold'};

const formatRupee = val =>
  val != null ? `₹ ${Number(val).toLocaleString('en-IN')}` : '—';

const StatusTag = ({status}) => (
  <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>{status}</Tag>
);
StatusTag.propTypes = {status: PropTypes.string};

const PaymentStatusTag = ({status}) => {
  const colorMap = {PAID: 'green', PENDING: 'orange', PARTIAL: 'blue'};
  return <Tag color={colorMap[status] ?? 'default'}>{status ?? '—'}</Tag>;
};
PaymentStatusTag.propTypes = {status: PropTypes.string};

const BillStatusTag = ({status}) => {
  const colorMap = {COMPLETED: 'green', DRAFT: 'gray', CANCELLED: 'red'};
  return <Tag color={colorMap[status] ?? 'default'}>{status ?? '—'}</Tag>;
};
BillStatusTag.propTypes = {status: PropTypes.string};

const CustomerDrawer = ({customer, open, onClose}) => {
  const [billTypeFilter, setBillTypeFilter] = useState(null);

  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const bills = useMemo(() => {
    if (!customer) { return []; }

    let filtered = customer.billInfo ?? [];

    // Date Range Filter
    if (startDate && endDate) {
      const start = startDate.startOf('day');
      const end   = endDate.endOf('day');

      filtered = filtered.filter(b => {
        const d = dayjs(b.createdAt);
        return (
          (d.isAfter(start) || d.isSame(start, 'day')) &&
        (d.isBefore(end)  || d.isSame(end,   'day'))
        );
      });
    }

    // GST / NON GST Filter
    if (billTypeFilter) {
      filtered = filtered.filter(bill => bill.billType === billTypeFilter);
    }

    return filtered;
  }, [customer, startDate, endDate, billTypeFilter]);

  const summary = useMemo(() => ({
    total: bills.length,
    finalAmt: bills.reduce((s, b) => s + (b.finalAmount ?? 0), 0),
    paid: bills.reduce((s, b) => s + (b.paidAmount ?? 0), 0),
    pending: bills.reduce((s, b) => s + (b.pendingAmount ?? 0), 0),
    taxAmt: bills.reduce((s, b) => s + (b.taxAmount ?? 0), 0),
    discount: bills.reduce((s, b) => s + (b.discountAmount ?? 0), 0),
  }), [bills]);

  const productSummary = useMemo(() => {
    const map = {};
    // Use filtered `bills` instead of all customer.billInfo
    bills.forEach(bill => {
      (bill.items ?? []).forEach(item => {
        const key = item.productName;
        if (!key) { return; }
        if (!map[key]) {
          map[key] = {productName: key, unit: item.unit ?? '', totalQty: 0, totalBags: 0};
        }
        map[key].totalQty  += item.quantity ?? 0;
        map[key].totalBags += item.bags     ?? 0;
      });
    });
    return Object.values(map).sort((a, b) => b.totalQty - a.totalQty);
  }, [bills]); // ← depends on bills, not customer

  if (!customer) {
    return null;
  }

  // Customer Bill Column
  const billCols = [
    {title: 'Bill #',    dataIndex: 'billNumber',     key: 'billNumber',     width: 130},

    {
      title: 'Type',
      dataIndex: 'billType',
      key: 'billType',
      width: 90,
      render: type => (
        <Tag color={type === 'GST' ? 'green' : 'orange'}>
          {type === 'NON_GST' ? 'Non GST' : 'GST'}
        </Tag>
      ),
    },

    {
      title: 'Items', dataIndex: 'items', key: 'items', width: 60,
      render: items => <Tag>{items?.length ?? 0}</Tag>,
    },
    {
      title: 'Final Amt', dataIndex: 'finalAmount', key: 'finalAmount', width: 120,
      render: v => <span className={'font-bold text-primary-700'}>{formatRupee(v)}</span>,
    },
    {
      title: 'Paid', dataIndex: 'paidAmount', key: 'paidAmount', width: 110,
      render: v => <span className={'font-medium text-green-700'}>{formatRupee(v)}</span>,
    },
    {
      title: 'Pending', dataIndex: 'pendingAmount', key: 'pendingAmount', width: 110,
      render: v => <span className={'font-medium text-red-600'}>{formatRupee(v)}</span>,
    },
    {
      title: 'Payment', dataIndex: 'paymentStatus', key: 'paymentStatus', width: 100,
      render: v => <PaymentStatusTag status={v} />,
    },
    {
      title: 'Status', dataIndex: 'billStatus', key: 'billStatus', width: 100,
      render: v => <BillStatusTag status={v} />,
    },
    {
      title: 'Method', dataIndex: 'paymentMethod', key: 'paymentMethod', width: 85,
      render: v => <Tag>{v}</Tag>,
    },
    {
      title: 'Date', dataIndex: 'createdAt', key: 'createdAt', width: 110,
      render: v => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
  ];

  const itemCols = [
    {
      title: 'Product', dataIndex: 'productName', key: 'productName', width: 160,
      render: v => <span className={'font-medium text-blue-700'}>{v}</span>,
    },
    {
      title: 'HSN Code', dataIndex: 'hsnCode', key: 'hsnCode', width: 130,
      render: v => v ? <Tag className={'font-mono text-xs'}>{v}</Tag> : '—',
    },
    {
      title: 'Unit', dataIndex: 'unit', key: 'unit', width: 70,
      render: v => v ? <span className={'text-blue-500 font-medium'}>{v}</span> : '—',
    },
    {
      title: 'Bags', dataIndex: 'bags', key: 'bags', width: 70,
      render: v => v != null ? v : '—',
    },
    {
      title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 70,
      render: v => <span className={'font-bold'}>{v}</span>,
    },
    {
      title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 110,
      render: v => formatRupee(v),
    },
    {
      title: 'Discount/Unit', dataIndex: 'discount', key: 'discount', width: 120,
      render: v => (v != null && v !== 0) ? formatRupee(v) : '—',
    },
    {
      title: 'CGST', dataIndex: 'cgst', key: 'cgst', width: 75,
      render: v => v != null ? <span className={'text-pink-500'}>{v}%</span> : '—',
    },
    {
      title: 'SGST', dataIndex: 'sgst', key: 'sgst', width: 75,
      render: v => v != null ? <span className={'text-pink-500'}>{v}%</span> : '—',
    },
    {
      title: 'IGST', dataIndex: 'igst', key: 'igst', width: 75,
      render: v => v != null ? <span className={'text-orange-500'}>{v}%</span> : '—',
    },
    {
      title: 'Line Total', dataIndex: 'totalPrice', key: 'totalPrice', width: 110,
      render: v => <span className={'font-bold text-blue-700'}>{formatRupee(v)}</span>,
    },
  ];

  // ── NEW: expanded row renderer 
  const expandedRowRender = record => {
    const items = record.items ?? [];
    if (!items.length) {
      return (
        <div className={'text-center text-gray-400 py-4'}>No items in this bill</div>
      );
    }
    return (
      <div className={'bg-primary-50 rounded-md py-4 border border-primary-100'}>
        <TableData
          columns={itemCols}
          dataSource={items.map((item, idx) => ({...item, key: item.productId ?? idx}))}
          size={'small'}
          pagination={false}
          scroll={{x: 'max-content'}}
          className={'nested-items-table'}
        />
      </div>
    );
  };

  return (
    <Drawer
      title={(
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold'}
            style={{
              background: customer.roles === 'VENDOR'
                ? 'linear-gradient(135deg, #3a5f98, #92a6c6)'
                : 'linear-gradient(135deg, #3a5f98, #92a6c6)',
            }}
          >
            {customer.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <p className={'font-bold text-gray-800 m-0 leading-tight'}>
              {customer.name}
              <Tag color={ROLE_COLORS[customer.roles]} className={'ml-2 text-xs'}>{customer.roles}</Tag>
            </p>
            <p className={'text-xs text-gray-400 m-0'}>{customer.businessName || customer.email}</p>
          </div>
        </div>
      )}
      open={open}
      onClose={onClose}
      size={1000}
      styles={{body: {padding: '16px 24px'}}}
    >
      <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5'}>
        {/* Mobile */}
        <div className={'flex items-center gap-3'}>
          <PhoneOutlined className={'text-blue-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Mobile</p>
            <p className={'font-medium m-0'}>{customer.mobile}</p>
          </div>
        </div>

        {/* Email */}
        <div className={'flex items-center gap-3'}>
          <MailOutlined className={'text-cyan-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Email</p>
            <p className={'font-medium m-0'}>
              {customer.email || '—'}
            </p>
          </div>
        </div>

        {/* Address */}
        <div className={'flex items-center gap-3'}>
          <EnvironmentOutlined className={'text-red-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Address</p>
            <p className={'font-medium m-0'}>
              {customer.address || '—'}
            </p>
          </div>
        </div>

        {/* Business */}
        <div className={'flex items-center gap-3'}>
          <ShopOutlined className={'text-purple-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Business</p>
            <p className={'font-medium m-0'}>
              {customer.businessName || '—'}
            </p>
          </div>
        </div>

        {/* GSTIN */}
        <div className={'flex items-center gap-3'}>
          <IdcardOutlined className={'text-orange-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>GSTIN</p>
            <p className={'font-medium font-mono text-base m-0'}>
              {customer.gstIn || '—'}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className={'flex items-center gap-3'}>
          <CheckCircleOutlined className={'text-green-500 text-lg'} />
          <div>
            <p className={'text-xs text-gray-400 m-0'}>Status</p>
            <StatusTag status={customer.status} />
          </div>
        </div>
      </div>

      <Divider className={'bg-primary-50'} />
 
      {/* Date Range Filter */}
      <div className={'flex items-center gap-3 mb-4 flex-wrap'}>
        <CalendarOutlined className={'text-primary-800'} />
        <span className={'text-sm font-semibold text-gray-500 uppercase tracking-wide'}>
    Filter Bills by Date:
        </span>

        <DatePicker.RangePicker
          value={[startDate, endDate]}
          onChange={vals => setDateRange(vals ?? [null, null])}
          allowClear
          format={'DD MMM YYYY'}
          disabledDate={current => current && current > dayjs().endOf('day')}
          placeholder={['From date', 'To date']}
          style={{width: 280}}
        />

        <Select
          className={'rounded-md'}
          allowClear
          placeholder={'Filter by bill type'}
          style={{width: 150}}
          value={billTypeFilter}
          onChange={value => setBillTypeFilter(value ?? null)}
        >
          <Option value={'GST'}><Tag color={'green'}>GST</Tag></Option>
          <Option value={'NON_GST'}><Tag color={'orange'}>Non GST</Tag></Option>
        </Select>
      </div>

      {/* Products Purchased Summary */}
      {productSummary.length > 0 && (
        <div className={'mb-5'}>
          <p className={'mb-1 text-gray font-semibold'}>  {`Products Purchased ${startDate && endDate
            ? `(${startDate.format('DD MMM')} – ${endDate.format('DD MMM YYYY')})`
            : '(All Time)'}`}</p>
          <div className={'flex flex-wrap gap-2'}>
            {productSummary.map(p => (
              <div
                key={p.productName}
                className={'flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-md px-3 py-2'}
              >
                <span className={'font-semibold text-gray-800 text-sm'}>{p.productName}</span>
                <span className={'text-xs text-primary-700 font-bold bg-white border border-primary-100 rounded-lg px-2 py-0.5'}>
                  {p.totalQty} {p.unit || 'units'}
                </span>
                {p.totalBags > 0 && (
                  <span className={'text-xs text-orange-600 font-medium'}>
              ({p.totalBags} bags)
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <Divider className={'bg-primary-50'} />

      {/* Summary cards */}
      <div className={'grid grid-cols-2 md:grid-cols-3 gap-3 mb-5'}> 
        
        {[
          {label: 'Total Bills',  value: summary.total,             color: 'text-primary-700',  bg: 'bg-primary-50  border-primary-200'},
          {label: 'Total Sales',  value: formatRupee(summary.finalAmt), color: 'text-emerald-700', bg: 'bg-emerald-50   border-green-200'},
          {label: 'Paid',         value: formatRupee(summary.paid),     color: 'text-blue-700',  bg: 'bg-blue-50    border-blue-200'},
          {label: 'Pending',      value: formatRupee(summary.pending),  color: 'text-red-600',   bg: 'bg-red-50     border-red-200'},
          {label: 'Tax',          value: formatRupee(summary.taxAmt),   color: 'text-slate-700',  bg: 'bg-slate-100    border-gray-200'},
          {label: 'Discount',     value: formatRupee(summary.discount), color: 'text-orange-600', bg: 'bg-orange-50  border-orange-200'},
        ].map(item => (
          <div key={item.label} className={`${item.bg} border rounded-xl px-4 py-3 text-center`}>
            <p className={'text-xs text-gray-400 m-0 mb-1'}>{item.label}</p>
            <p className={`font-bold text-lg m-0 ${item.color}`}>{item.value}</p>
          </div>
        ))}
      </div>

      {/* Bills Table */}
      <div>
        <p className={'text-xs font-semibold text-gray pt-4 uppercase tracking-widest mb-3'}>
         Bill History {startDate && endDate
            ? `— ${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`
            : '(All)'}
        </p>

        {bills.length ? (
          <TableData
            columns={billCols}
            dataSource={bills.map((b, i) => ({...b, key: b.id ?? i}))}
            size={'small'}
            scroll={{x: 'max-content'}}
            locale={{emptyText: 'No bills found'}}
            expandable={{
              expandedRowRender,
              rowExpandable: record => (record.items?.length ?? 0) > 0,
            }}
          />
        ) : (
          <div className={'bg-primary-50 border border-dashed border-primary-100 rounded-md py-8 text-center text-primary-800'}>
            <ShopOutlined style={{fontSize: 28, marginBottom: 8, display: 'block'}} />
            {startDate && endDate
              ? `No bills from ${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`
              : 'No bill records found'}
          </div>
        )}
      </div>
    </Drawer>
  );
};

CustomerDrawer.propTypes = {
  customer: PropTypes.object,
  open:     PropTypes.bool,
  onClose:  PropTypes.func,
};

const CustomerTab = () => {
  const auth  = useSelector(selectAuth);
  const token = auth?.tokenData;

  const [roleFilter,   setRoleFilter]   = useState(null);
  const [drawerState,  setDrawerState]  = useState({open: false, customer: null});

  const {data, isLoading, refetch} = RqAccountantLookupAll(token);
  const [editModal, setEditModal] = useState({
    open: false,
    employee: null,
  });

  const customers = useMemo(() => {
    if (!data?.length) {return [];}
    return data
      .filter(u => ['CUSTOMER', 'VENDOR'].includes(u.roles))
      .filter(u => !roleFilter || u.roles === roleFilter)
      .map((u, i) => ({
        key:          u.id,
        id:           u.id,
        srno:         i + 1,
        name:         `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        email:        u.email,
        mobile:       u.mobile,
        address:      u.address,
        street: u.street,
        apartment: u.apartment,
        pinCode: u.pinCode,
        city: u.city,
        district: u.district,
        state: u.state,
        roles:        u.roles,
        status:       u.status,
        verifcation:  u.verifcation,
        gstIn:        u.gstIn        ?? '',
        businessName: u.businessName ?? '',
        ldnumber: u.ldnumber ?? '',
        billInfo:     u.billInfo     ?? [],
      }));
  }, [data, roleFilter]);

  const stats = useMemo(() => {
    const all  = data?.filter(u => ['CUSTOMER', 'VENDOR'].includes(u.roles)) ?? [];
    return {
      total:     all.length,
      customers: all.filter(u => u.roles === 'CUSTOMER').length,
      vendors:   all.filter(u => u.roles === 'VENDOR').length,
      active:    all.filter(u => u.status === 'ACTIVE').length,
    };
  }, [data]);
  
  const {mutate: updateEmployee, isLoading: isUpdating} =
  RqAccountantUpdate({
    onSuccess: () => {
      toast.success('Employee updated');
      refetch();
      setEditModal({open: false, employee: null});
    },
    onError: err =>
      toast.error(err?.message || 'Update failed'),
  });

  const {mutate: deleteEmployee} =
  RqDeleteUser({
    onSuccess: () => {
      toast.success('Employee deleted');
      refetch();
    },
    onError: err =>
      toast.error(err?.message || 'Delete failed'),
  });

  const columns = [
    {title: 'Sr.',  dataIndex: 'srno',  key: 'srno',  width: 55},
    {
      title: 'Name', dataIndex: 'name', key: 'name', width: 160,
      render: v => <span className={'font-medium text-gray-800'}>{v}</span>,
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email', width: 200, ellipsis: true,
      render: v => <Tooltip title={v}><span className={'text-gray-600'}>{v}</span></Tooltip>,
    },
    {title: 'Mobile', dataIndex: 'mobile', key: 'mobile', width: 120},
   
    {
      title: 'Business', dataIndex: 'businessName', key: 'businessName', width: 160,
      render: v => v || <span className={'text-gray-400'}>—</span>,
    },

    {
      title: 'LD Number', dataIndex: 'ldnumber', key: 'ldnumber', width: 160,
      render: v => v || <span className={'text-gray-400'}>—</span>,
    },
    {
      title: 'GSTIN', dataIndex: 'gstIn', key: 'gstIn', width: 165,
      render: v => v
        ? <span className={'font-mono text-xs'}>{v}</span>
        : <span className={'text-gray-400'}>—</span>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 95,
      render: status => <StatusTag status={status} />,
    },
    {
      title: 'Total Bills', key: 'billCount', width: 100,
      render: (_, r) => <Tag color={'blue'}>{r.billInfo.length}</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <button
          className={'flex gap-2'}
          onClick={e => e.stopPropagation()}
        >
          <Tooltip title={'Edit'}>
            <EditOutlined
              className={'cursor-pointer border rounded-md p-1 text-blue-600'}
              onClick={() =>
                setEditModal({
                  open: true,
                  employee: record,
                })
              }
            />
          </Tooltip>

          <Tooltip title={'Delete'}>
            <Popconfirm
              title={'Delete Employee'}
              description={'Are you sure?'}
              okText={'Delete'}
              cancelText={'Cancel'}
              okButtonProps={{danger: true}}
              onConfirm={() =>
                deleteEmployee({
                  token,
                  id: record.id,
                })
              }
            >
              <DeleteOutlined
                className={'cursor-pointer border rounded-md p-1 text-red-600'}
              />
            </Popconfirm>
          </Tooltip>
        </button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center h-64'}>
        <Spin size={'large'} />
      </div>
    );
  }

  return (
    <div className={'md:p-4 p-1'}>
      {/* Stats */}
      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5'}>
        <StatCard label={'Total'}     value={stats.total}     icon={<ShopOutlined className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Customers'} value={stats.customers} icon={<ShopOutlined className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Vendors'}   value={stats.vendors}   icon={<ShopOutlined className={'bg-color'} />} bg={'#e6e9ec'} />
        <StatCard label={'Active'}    value={stats.active}    icon={<ShopOutlined className={'bg-color'} />} bg={'#e6e9ec'} />
      </div>

      {/* Role Filter */}
      <div className={'flex items-center gap-3 mb-4 flex-wrap'}>
        <Select
          allowClear
          placeholder={'Filter by role'}
          style={{width: 150}}
          value={roleFilter}
          onChange={v => setRoleFilter(v ?? null)}
        >
          <Option value={'CUSTOMER'}><Tag color={'green'}>Customer</Tag></Option>
          <Option value={'VENDOR'}><Tag color={'gold'}>Vendor</Tag></Option>
        </Select>
        <span className={'text-xs text-gray-400'}>
          Click any row to view bills &amp; payment history
        </span>
      </div>

      {/* Table */}
      <div className={'overflow-x-auto rounded-lg '}>
        <TableData
          columns={columns}
          dataSource={customers}
          rowKey={'id'}
          size={'middle'}
          scroll={{x: 'max-content'}}
          rowClassName={' transition-colors cursor-pointer'}
          locale={{emptyText: 'No customers or vendors found'}}
          onRow={record => ({
            onClick: () => setDrawerState({open: true, customer: record}),
          })}
        />
      </div>

      {/* Detail Drawer */}
      <CustomerDrawer
        open={drawerState.open}
        customer={drawerState.customer}
        onClose={() => setDrawerState({open: false, customer: null})}
      />

      <EditEmployeeScreen
        open={editModal.open}
        employee={editModal.employee}
        token={token}
        loggedInRole={auth?.user?.role}
        onSave={updateEmployee}
        isUpdating={isUpdating}
        onClose={() =>
          setEditModal({
            open: false,
            employee: null,
          })
        }
      />
    </div>
  );
};

export {CustomerTab};
export default CustomerTab;
